import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateUserData } from '../app/auth/authSlice';
import userDataService from '../services/userDataService';

/**
 * Custom hook to safely access profile and settings data
 * Handles loading states and retries to prevent "data not available" errors
 */
export const useProfileAndSettings = () => {
  const dispatch = useDispatch();
  const { profileData, settingsData, isAuthenticated, lastDataRefresh } = useSelector(state => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  useEffect(() => {
    // Don't do anything if not authenticated
    if (!isAuthenticated) {
      return;
    }

    // Check if we have any data
    const hasAnyData = profileData || settingsData;
    
    // Check if data is stale (older than 1 hour)
    const isDataStale = lastDataRefresh ? 
      (Date.now() - new Date(lastDataRefresh).getTime()) > (60 * 60 * 1000) : 
      true;

    // Load data if we don't have it or if it's stale
    if (!hasAnyData || (isDataStale && !isLoading)) {
      loadUserData();
    } else if (hasAnyData) {
      setHasInitiallyLoaded(true);
    }
  }, [isAuthenticated, profileData, settingsData, lastDataRefresh]);

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      const userData = await userDataService.fetchUserData();
      dispatch(updateUserData(userData));
      setHasInitiallyLoaded(true);
    } catch (error) {
      console.error('Failed to load user data:', error);
      // Mark initial load as attempted so pages know not to show "still loading" as error
      setHasInitiallyLoaded(true);
    } finally {
      setIsLoading(false);
    }
  }

  return {
    profileData,
    settingsData,
    isLoading,
    hasInitiallyLoaded,
    refreshData: loadUserData,
    // Safe getters that return defaults if data not available
    getCompCurrency: () => profileData?.compCurrency || 'GBP',
    getVATRate: () => settingsData?.s_VAT || 0,
    isTaxEnabled: () => settingsData?.isTaxEnabled || false,
  };
};

export default useProfileAndSettings;
